import React, { useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import InviteModal from '../../components/shared/InviteModal';
import { invitesAPI } from '../../api/invites';
import { logger } from '../../utils/logger';

type InviteScreenRouteProp = RouteProp<RootStackParamList, 'Invite'>;
type InviteScreenNavigationProp = NativeStackNavigationProp<
  RootStackParamList,
  'Invite'
>;

const InviteScreen: React.FC = () => {
  const navigation = useNavigation<InviteScreenNavigationProp>();
  const route = useRoute<InviteScreenRouteProp>();
  const { colors } = useTheme();
  const { user } = useAuth();
  const code = route.params?.code;

  // Logged-in user: generate invite link for sharing (when no code in route)
  const inviteCode = useMemo(() => {
    if (!user?.id) return '';
    const generateCode = (input: string): string => {
      let hash = 0;
      for (let i = 0; i < input.length; i++) {
        const char = input.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
      }
      const hashStr = Math.abs(hash).toString(36).toUpperCase();
      const userIdStr = user.id.toString().padStart(4, '0');
      return (hashStr + userIdStr).substring(0, 12);
    };
    return generateCode(`invite_${user.id}_${user.email || 'user'}`);
  }, [user?.id, user?.email]);

  const inviteLink = useMemo(() => {
    if (!inviteCode) return '';
    const baseUrl =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.origin
        : 'https://mycoloursense.com';
    return `${baseUrl}/invite/${inviteCode}`;
  }, [inviteCode]);

  // Incoming invite (deep link): redirect to Register with invite code
  useEffect(() => {
    if (code) {
      navigation.replace('Register', { inviteCode: code });
    }
  }, [code, navigation]);

  // Ensure invite exists in backend when showing share screen
  useEffect(() => {
    if (!code && user && inviteCode) {
      invitesAPI.create(inviteCode).catch((error: unknown) => {
        const err = error as Error;
        if (!err.message?.includes('already exists')) {
          logger.error('Failed to save invite code:', err);
        }
      });
    }
  }, [code, user, inviteCode]);

  // Incoming invite: show loading while redirecting
  if (code) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={[styles.text, { color: colors.text }]}>
          Redirecting to sign up...
        </Text>
      </View>
    );
  }

  // No code + not logged in: invalid or expired invite
  if (!user) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <Ionicons name="link-outline" size={48} color={colors.secondary} />
        <Text style={[styles.title, { color: colors.text }]}>
          Invalid or expired invite
        </Text>
        <Text style={[styles.text, { color: colors.secondary }]}>
          Please ask for a new invite link.
        </Text>
        <TouchableOpacity
          style={[styles.button, { backgroundColor: colors.primary }]}
          onPress={() => navigation.goBack()}
          activeOpacity={0.8}
        >
          <Text style={styles.buttonText}>Go back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // No code + logged in: show share-invite modal (has its own header and close)
  if (!inviteLink) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={[styles.text, { color: colors.text }]}>
          Preparing your invite link...
        </Text>
      </View>
    );
  }
  return (
    <InviteModal
      visible={true}
      onClose={() => navigation.goBack()}
      inviteLink={inviteLink}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  text: {
    marginTop: 16,
    fontSize: 16,
    textAlign: 'center',
  },
  title: {
    marginTop: 16,
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
  },
  button: {
    marginTop: 24,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignSelf: 'center',
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default InviteScreen;
